import { useUpdateSurfacePreferences } from "@/components/preferences/usePreferences.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Toggle } from "@/components/ui/toggle.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { useCallback, useState } from "react";
import { ShortcutBindingsEditor } from "./ShortcutBindingsEditor.tsx";
import { useKeyboardShortcutsEnabled } from "./useKeyboardShortcuts.ts";
import { useShortcut } from "./useShortcut.ts";

export const KeyboardShortcutsOverlay = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const keyboardShortcutsEnabled = useKeyboardShortcutsEnabled();
  const updateKeyboardPreferences = useUpdateSurfacePreferences("keyboard");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleToggle = useCallback(
    async (checked: boolean) => {
      setIsUpdating(true);
      try {
        await updateKeyboardPreferences({
          shortcutsEnabled: checked ? "enabled" : "disabled",
        });
      } catch {
        errorToast("Failed to update keyboard shortcut preferences. Please try again.");
      } finally {
        setIsUpdating(false);
      }
    },
    [updateKeyboardPreferences],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Capped and column-flexed so the list below scrolls rather than the card
          growing past the viewport: DialogContent is `fixed` and centred, so
          overflow is clipped at BOTH ends with no way to scroll to it — the
          title and the enable toggle would be the first things to go. */}
      {/* A tenth wider than the stock card: a row's keycaps plus their controls
          need the room before they wrap. Still capped to the viewport. */}
      <DialogContent className="flex max-h-[85vh] max-w-[min(35.2rem,calc(100vw-2.5rem))] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription className="sr-only">
            Available keyboard shortcuts, each editable in place
          </DialogDescription>
        </DialogHeader>
        {/* `min-h-0` is load-bearing: a flex child's default `min-height:auto`
            refuses to shrink below its content, so without it this never
            scrolls and the cap just clips. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden mt-1">
          <ShortcutBindingsEditor />
        </div>
        {/* Pinned below the scroll region — it's the only way back to enabled
            once shortcuts are off, which is the state `?` exists to rescue. */}
        <div className="flex shrink-0 items-center justify-between pt-3 border-t border-border">
          <span className="text-sm text-muted-foreground">Keyboard shortcuts</span>
          <Toggle
            checked={keyboardShortcutsEnabled}
            onCheckedChange={handleToggle}
            disabled={isUpdating}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Mounts the `?` hotkey listener and the overlay dialog.
 * Must be rendered inside a component that has access to React Query context.
 */
export const KeyboardShortcutsManager = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  // `system.help` is `alwaysOn`, so `?` opens the overlay even while shortcuts
  // are disabled — the discovery path back to enabling them.
  useShortcut("system.help", () => setOpen(true), { deps: [setOpen] });

  return <KeyboardShortcutsOverlay open={open} onOpenChange={setOpen} />;
};
